/**
 * Minimal HTTP client for the Personal-Area API — a trimmed sibling of
 * `apps/captive-portal/src/api/client.ts`, keeping the same error-envelope contract
 * (`{"error":{code,message,details}}` → thrown as `ApiError` with `.code`/`.details`).
 *
 * Two differences from that sibling, both deliberate:
 *
 *  1. `credentials: 'same-origin'` is stated EXPLICITLY. It is also the fetch default,
 *     and that is exactly why it is written down: the session here is an HttpOnly
 *     cookie (`billing_pa_session`), so a later "cleanup" that set `credentials:
 *     'omit'` — which the captive portal could afford, because it authenticates with a
 *     token — would log every subscriber out on the next request with no error to
 *     read. `'same-origin'` and not `'include'`: the SPA is served from the portal
 *     listener's own root, so there is no cross-origin case to admit, and admitting
 *     one would weaken the CSRF position the origin check + SameSite=Lax cookie hold.
 *
 *  2. No absolute base URL and no environment-configurable host. `/api/v1` is a
 *     relative path because the bundle and the API share an origin by construction
 *     (internal/app/pa_server.go). An absolute URL here would also be an external
 *     reference — see src/bundle.test.ts.
 *
 * There is no global 401 → redirect and no error notifier: a 401 is a NORMAL answer on
 * this surface (the session slid past its idle TTL) and the session provider turns it
 * into the login screen. A redirect installed here would fight that.
 */

export const API_BASE = '/api/v1';

export interface ApiErrorEnvelope {
  code: string;
  message: string;
  details?: unknown;
}

/** Normalised error thrown by every failed request. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  /** True when the request never reached the server (offline / DNS). */
  readonly isNetworkError: boolean;

  constructor(status: number, envelope: ApiErrorEnvelope, isNetworkError = false) {
    super(envelope.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = envelope.code;
    this.details = envelope.details;
    this.isNetworkError = isNetworkError;
  }
}

async function parseErrorEnvelope(res: Response): Promise<ApiErrorEnvelope> {
  try {
    const body = (await res.json()) as { error?: ApiErrorEnvelope };
    if (body && body.error && typeof body.error.message === 'string') {
      return body.error;
    }
  } catch {
    // response had no / malformed JSON body
  }
  return { code: `http_${res.status}`, message: `HTTP ${res.status}` };
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * isUpload reports a `FormData` body — the ONE non-JSON request shape this app has.
 *
 * ⚠ TWO THINGS MUST NOT HAPPEN TO A MULTIPART BODY, and both are silent. It must not be
 * `JSON.stringify`d (which turns a chosen file into the string «{}» and uploads nothing),
 * and this client must not set `Content-Type`: the browser writes it WITH the multipart
 * BOUNDARY, and a hand-written `multipart/form-data` header without one makes the server's
 * reader fail to find any part at all — `pa.attachment_required` on a request that carried
 * the file. So the header is omitted here deliberately rather than by oversight.
 *
 * `typeof FormData !== 'undefined'` because this module is also loaded by the node-side
 * mechanical guards, where the global may be absent.
 */
function isUpload(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;
  const upload = isUpload(body);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !upload ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : upload ? body : JSON.stringify(body),
      ...rest,
    });
  } catch {
    throw new ApiError(0, { code: 'network_error', message: 'network_error' }, true);
  }

  if (!res.ok) {
    const envelope = await parseErrorEnvelope(res);
    throw new ApiError(res.status, envelope);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

/** Thin verb helpers over apiFetch. */
export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  /**
   * upload POSTs `multipart/form-data` — the two ticket-attachment routes and nothing
   * else (`POST /pa/tickets/{id}/files`, `POST /pa/tickets/{id}/messages/{mid}/files`).
   *
   * It goes through `apiFetch` rather than around it so that a refusal arrives as the
   * same `ApiError` every other call throws: an attachment can be refused with
   * `413 pa.attachment_too_large` (`details.max_bytes`), `400 pa.attachment_invalid`,
   * `409 pa.tickets_disabled` and `503 pa.attachments_unavailable`, and each of those is a
   * sentence the server wrote for the subscriber to read.
   *
   * ⚠ ~~`pa.storage_unavailable`~~ UNTIL 2026-08-12 (the second review fix pass): that code is
   * the DOCUMENT download's and its message promises that «документ сформирован, но хранилище
   * недоступно» — read by a subscriber attaching a photograph of a fault to a support ticket,
   * about a document he never asked for. The code was split, not the message.
   */
  upload: <T>(path: string, form: FormData, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body: form }),
  /**
   * DELETE — one route uses it, `DELETE /pa/payments/saved-card` (W3).
   *
   * It carries no body, because the one caller has nothing to say beyond the contract
   * that is already in the query string: this surface proves ownership through
   * `?contract_id=` on every method, which is what keeps ONE gate in front of every
   * route rather than a second door in a request body.
   */
  del: <T>(path: string, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
